import { BrowserModule, Title } from '@angular/platform-browser';
import {
	APP_INITIALIZER,
	CUSTOM_ELEMENTS_SCHEMA,
	ErrorHandler,
	NgModule,
	provideZoneChangeDetection,
} from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TitleCasePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { loginReducer } from './components/authentication/store/reducers/login.reducer';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { LoginEffects } from './components/authentication/store/effects/login.effect';
import { AuthModule } from './components/authentication/auth.module';
import { IconDialogComponent } from './components/controls/dialogs/icon-dialog/icondialog.component';
import { PageNotFoundComponent } from './components/error-pages/page-not-found/page-not-found.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { environment } from '../environments/environment';

import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormatCurrencyUnitsPipe } from './pipes/format-currency-units/format-currency-units.pipe';
import { CssClassNameForNumberSignPipe } from './pipes/css-class-name-for-number-sign/css-class-name-for-number-sign.pipe';
import { HttpInterceptorSpinLoader } from './util/http/http-interceptor-spin-loader';
import { HttpInterceptorHandleErrors } from './util/http/http-interceptor-handle-errors';
import { HttpInterceptorWithCredentials } from './util/http/http-interceptor-with-credentials';
import { HttpInterceptorSetXsrf } from './util/http/http-interceptor-set-xsrf';
import { IdsNotificationsModule } from './components/controls/notifications/notifications.module';
import { ServerErrorComponent } from './components/error-pages/server-error/server-error.component';
import { ForbiddenComponent } from './components/error-pages/forbidden/forbidden.component';
import { UnauthorizedComponent } from './components/error-pages/unauthorized/unauthorized.component';
import { BadGatewayComponent } from './components/error-pages/bad-gateway/bad-gateway.component';
import { GlobalErrorHandler } from './global-error-handler';
import { ConfirmationService } from 'primeng/api';

import { LetDirective } from '@ngrx/component';

import { DialogService } from 'primeng/dynamicdialog';
import { LabelByJurisdictionPipe } from './pipes/jurisdiction/label-by-jurisdiction.pipe';
import { HideByJurisdictionPipe } from './pipes/jurisdiction/hide-by-jurisdiction.pipe';
import { AppSettingService } from './app-setting.service';
import { CustomRouteReuseStrategy } from './util/custom-route-reuse.strategy';
import { migrateLegacyAgGridLocalStorageKeys } from './components/shared/local-storage-migration';

export const appSettingFactory = (appSettingService: AppSettingService) => {
	return () => appSettingService.loadConfig();
};

@NgModule({
	declarations: [],
	imports: [
		AppComponent,
		BrowserModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		AuthModule,
		RouterModule,
		NgxIntlTelInputModule,
		ProgressSpinnerModule,
		StoreModule.forRoot({ login: loginReducer }),
		EffectsModule.forRoot([LoginEffects]),
		StoreRouterConnectingModule.forRoot(),
		StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
		ReactiveFormsModule,
		FormsModule,
		AutoCompleteModule,
		IdsNotificationsModule,
		LetDirective,
		IconDialogComponent,
		PageNotFoundComponent,
		FormatCurrencyUnitsPipe,
		CssClassNameForNumberSignPipe,
		ServerErrorComponent,
		ForbiddenComponent,
		UnauthorizedComponent,
		BadGatewayComponent,
	],
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		{
			provide: APP_INITIALIZER,
			useFactory: () => migrateLegacyAgGridLocalStorageKeys,
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: appSettingFactory,
			deps: [AppSettingService],
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorSpinLoader,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorWithCredentials,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorSetXsrf,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: HttpInterceptorHandleErrors,
			multi: true,
		},
		Title,
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler,
		},
		ConfirmationService,
		DialogService,
		LabelByJurisdictionPipe,
		HideByJurisdictionPipe,
		TitleCasePipe,
		{ provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
		provideHttpClient(withInterceptorsFromDi()),
	],
	bootstrap: [AppComponent],
	schemas: [
		CUSTOM_ELEMENTS_SCHEMA, // Tells Angular we will have custom tags in our templates
	],
})
export class AppModule {}

